/**
 * Persistent last-update strip above the dock tabs.
 *
 * The activity waterfall used to be the only place an operator saw a new
 * alert. Once that feed is a tab, leaving it would hide the thing that
 * just fired. This bar stays put and names the worst open condition.
 */
import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import FlightIcon from '@mui/icons-material/Flight';
import {
  MissionProgress,
  MissionRouteEvent,
  MissionRouteStatus,
  MissionTimelineStop,
  acknowledgeRouteEvents,
} from '../../services/missionApi';
import { buildDockBanner } from './alertWaterfall';
import { formatClock } from './format';

const CORAL = '#ef5350';
const GOLD = '#f0b429';
const MUTED = '#8a939f';

export interface MissionDockBannerProps {
  missionId: number;
  events: MissionRouteEvent[];
  deviceLabel: (deviceId: string) => string;
  units?: 'km' | 'mi';
  progress?: MissionProgress | null;
  timeline?: MissionTimelineStop[];
  onAcked: (status: MissionRouteStatus) => void;
  onFocus?: (longitude: number, latitude: number) => void;
}

function legLabel(
  progress?: MissionProgress | null,
  timeline?: MissionTimelineStop[],
): string | null {
  if (!timeline || timeline.length < 2) return null;
  const next = timeline.findIndex((s) => !s.reached_at);
  if (next < 0) return null;
  const leg = Math.max(1, next);
  return `leg ${leg}`;
}

const MissionDockBanner: React.FC<MissionDockBannerProps> = ({
  missionId,
  events,
  deviceLabel,
  units = 'mi',
  progress,
  timeline,
  onAcked,
  onFocus,
}) => {
  const [busy, setBusy] = useState(false);
  const banner = useMemo(
    () => buildDockBanner(events, deviceLabel, units),
    [events, deviceLabel, units],
  );
  const alert = banner.kind === 'alert';
  const tone = banner.tone === 'crit' ? CORAL : banner.tone === 'warn' ? GOLD : MUTED;
  const locatable = banner.latitude != null && banner.longitude != null;
  const meta = [
    formatClock(banner.at),
    legLabel(progress, timeline),
    banner.queued > 0
      ? `${banner.queued} lower-severity queued`
      : null,
  ].filter(Boolean).join(' · ');

  const ack = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!banner.eventId || busy) return;
    setBusy(true);
    try {
      onAcked(await acknowledgeRouteEvents(missionId, banner.eventId));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      role={locatable ? 'button' : undefined}
      tabIndex={locatable ? 0 : undefined}
      onClick={() => {
        if (locatable) onFocus?.(banner.longitude!, banner.latitude!);
      }}
      onKeyDown={(e) => {
        if (!locatable) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onFocus?.(banner.longitude!, banner.latitude!);
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.85,
        px: 1.1,
        py: 0.7,
        flexShrink: 0,
        cursor: locatable ? 'pointer' : 'default',
        bgcolor: alert
          ? (banner.tone === 'crit' ? 'rgba(239,83,80,0.14)' : 'rgba(240,180,41,0.10)')
          : 'rgba(255,255,255,0.03)',
        borderBottom: '1px solid',
        borderColor: alert
          ? (banner.tone === 'crit' ? 'rgba(239,83,80,0.35)' : 'rgba(240,180,41,0.28)')
          : 'divider',
      }}
    >
      {banner.eventType === 'uas_intercept' ? (
        <FlightIcon sx={{ fontSize: 15, color: tone, flexShrink: 0, transform: 'rotate(-45deg)' }} />
      ) : (
        <Box sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          flexShrink: 0,
          bgcolor: tone,
          boxShadow: alert ? `0 0 8px ${tone}` : 'none',
        }}
        />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.72rem',
          fontWeight: 800,
          color: alert ? tone : '#d5dbe2',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        >
          {banner.title}
          {banner.detail ? ` · ${banner.detail}` : ''}
        </Typography>
        <Typography sx={{ display: 'block', fontSize: '0.58rem', color: MUTED }}>
          {meta || banner.detail || 'Last update'}
        </Typography>
      </Box>
      {alert && banner.eventId && (
        <Box
          component="button"
          type="button"
          disabled={busy}
          onClick={ack}
          sx={{
            height: 22,
            px: 1.1,
            borderRadius: 4,
            border: `1px solid ${banner.tone === 'crit' ? 'rgba(239,83,80,0.45)' : 'rgba(240,180,41,0.4)'}`,
            bgcolor: 'rgba(0,0,0,0.22)',
            color: '#f3f6f8',
            font: 'inherit',
            fontSize: '0.62rem',
            fontWeight: 800,
            cursor: busy ? 'default' : 'pointer',
            flexShrink: 0,
          }}
        >
          {busy ? '…' : 'Ack'}
        </Box>
      )}
    </Box>
  );
};

export default MissionDockBanner;
